import { useState, useEffect } from 'react';
import AdminLayout from '@/components/admin/AdminLayout';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { Mail, Phone, Calendar, MessageSquare, CheckCircle, Plus } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { value: 'peticion_presupuesto', label: 'Petición Presupuesto', color: 'bg-blue-100 text-blue-800' },
  { value: 'contestado_pendiente_confirmacion', label: 'Contestado (Pendiente Confirmación)', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'cerrado', label: 'Cerrado', color: 'bg-green-100 text-green-800' },
  { value: 'cancelado', label: 'Cancelado', color: 'bg-red-100 text-red-800' }
];

const CONTACT_PREFERENCES = {
  'llamada': 'Llamada',
  'videollamada': 'Videollamada (WhatsApp)',
  'presencial': 'Reunión presencial'
};

export default function AdminMessages() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [trips, setTrips] = useState([]);
  const [bookingData, setBookingData] = useState({
    trip_id: '',
    trip_title: '',
    travel_date: '',
    end_date: '',
    num_travelers: 1,
    special_requests: '',
    budget: '',
    price: ''
  });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    const fetchMessages = async () => {
      try {
        const res = await axios.get(`${API}/contact/messages`, { headers });
        setMessages(res.data);
      } catch (err) {
        console.error('Error fetching messages:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchMessages();
  }, []);

  useEffect(() => {
    const fetchTrips = async () => {
      try {
        const res = await axios.get(`${API}/trips`);
        setTrips(res.data);
      } catch (err) {
        console.error('Error fetching trips:', err);
      }
    };
    fetchTrips();
  }, []);

  const handleStatusChange = async (messageId, newStatus) => {
    try {
      await axios.put(
        `${API}/contact/messages/${messageId}/status`,
        { status: newStatus },
        { headers }
      );
      setMessages(messages.map(msg => 
        msg.id === messageId ? { ...msg, status: newStatus } : msg
      ));
      toast.success('Estado actualizado correctamente');
    } catch (err) {
      console.error('Error updating status:', err);
      toast.error('Error al actualizar el estado');
    }
  };

  const handleOpenBookingDialog = (message) => {
    setSelectedMessage(message);
    setBookingData({
      trip_id: '',
      trip_title: '',
      travel_date: '',
      end_date: '',
      num_travelers: 1,
      special_requests: message.message || '',
      budget: '',
      price: ''
    });
    setOpenDialog(true);
  };

  const handleCreateBooking = async () => {
    if (!selectedMessage) return;
    
    try {
      const res = await axios.post(
        `${API}/bookings/from-message/${selectedMessage.id}`,
        bookingData,
        { headers }
      );
      
      // Actualizar el mensaje a cerrado en la lista local
      setMessages(messages.map(msg => 
        msg.id === selectedMessage.id ? { ...msg, status: 'cerrado' } : msg
      ));
      
      toast.success('Reserva creada correctamente');
      setOpenDialog(false);
      setSelectedMessage(null);
    } catch (err) {
      console.error('Error creating booking:', err);
      toast.error('Error al crear la reserva');
    }
  };

  const formatDate = (dateStr) => {
    try {
      return format(new Date(dateStr), "d MMM yyyy, HH:mm", { locale: es });
    } catch {
      return dateStr;
    }
  };

  const getStatusBadge = (status) => {
    const statusOption = STATUS_OPTIONS.find(opt => opt.value === status);
    if (!statusOption) return null;
    return (
      <Badge className={statusOption.color}>
        {statusOption.label}
      </Badge>
    );
  };

  return (
    <AdminLayout>
      <div className="mb-8">
        <h1 className="text-3xl font-serif font-semibold text-primary" data-testid="admin-messages-title">
          Mensajes de Contacto
        </h1>
        <p className="text-muted-foreground">
          {messages.length} mensajes recibidos
        </p>
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="skeleton h-32 rounded-lg" />
            ))}
          </div>
        ) : messages.length > 0 ? (
          <div className="divide-y" data-testid="messages-list">
            {messages.map((message, index) => (
              <div key={message.id} className="p-6" data-testid={`message-${index}`}>
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-primary">
                        {message.name}
                      </h3>
                      {getStatusBadge(message.status || 'peticion_presupuesto')}
                    </div>
                    <p className="text-secondary font-medium mb-2">{message.subject}</p>
                    
                    <div className="flex items-center gap-3">
                      <label className="text-sm text-muted-foreground">Estado:</label>
                      <Select
                        value={message.status || 'peticion_presupuesto'}
                        onValueChange={(value) => handleStatusChange(message.id, value)}
                      >
                        <SelectTrigger className="w-64">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUS_OPTIONS.map(option => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <span className="text-sm text-muted-foreground flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {formatDate(message.created_at)}
                  </span>
                </div>

                <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mb-4">
                  <span className="flex items-center gap-1">
                    <Mail className="w-4 h-4" />
                    <a href={`mailto:${message.email}`} className="hover:text-primary">
                      {message.email}
                    </a>
                  </span>
                  {message.phone && (
                    <span className="flex items-center gap-1">
                      <Phone className="w-4 h-4" />
                      <a href={`tel:${message.phone}`} className="hover:text-primary">
                        {message.phone}
                      </a>
                    </span>
                  )}
                  {message.contact_preference && (
                    <span className="flex items-center gap-1 text-purple-600">
                      <CheckCircle className="w-4 h-4" />
                      Preferencia: {CONTACT_PREFERENCES[message.contact_preference] || message.contact_preference}
                    </span>
                  )}
                </div>

                <div className="bg-slate-50 p-4 rounded-lg mb-4">
                  <div className="flex items-start gap-2">
                    <MessageSquare className="w-4 h-4 text-muted-foreground mt-1 flex-shrink-0" />
                    <p className="text-muted-foreground whitespace-pre-wrap">{message.message}</p>
                  </div>
                </div>

                {/* Botón Crear Reserva - solo si no está cerrado o cancelado */}
                {message.status !== 'cerrado' && message.status !== 'cancelado' && (
                  <div className="flex justify-end">
                    <Button 
                      onClick={() => handleOpenBookingDialog(message)}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Crear Reserva
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center text-muted-foreground">
            No hay mensajes todavía
          </div>
        )}
      </div>

      {/* Diálogo para crear reserva */}
      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Crear Reserva desde Mensaje</DialogTitle>
            <DialogDescription>
              Cliente: {selectedMessage?.name} ({selectedMessage?.email})
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label htmlFor="trip_id">Viaje (Opcional)</Label>
                <Select 
                  value={bookingData.trip_id} 
                  onValueChange={(value) => {
                    const selectedTrip = trips.find(t => t.id === value);
                    setBookingData({ 
                      ...bookingData, 
                      trip_id: value,
                      trip_title: selectedTrip ? selectedTrip.title : bookingData.trip_title
                    });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona un viaje (opcional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {trips.map(trip => (
                      <SelectItem key={trip.id} value={trip.id}>
                        {trip.title} - {trip.destination}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="col-span-2">
                <Label htmlFor="trip_title">Título del Viaje *</Label>
                <Input 
                  id="trip_title"
                  value={bookingData.trip_title}
                  onChange={(e) => setBookingData({ ...bookingData, trip_title: e.target.value })}
                  placeholder="Ej: Viaje a Japón personalizado"
                  required
                />
              </div>

              <div>
                <Label htmlFor="travel_date">Fecha inicio *</Label>
                <Input 
                  id="travel_date"
                  type="date"
                  value={bookingData.travel_date}
                  onChange={(e) => setBookingData({ ...bookingData, travel_date: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label htmlFor="end_date">Fecha fin</Label>
                <Input 
                  id="end_date"
                  type="date"
                  value={bookingData.end_date}
                  onChange={(e) => setBookingData({ ...bookingData, end_date: e.target.value })}
                />
              </div>

              <div>
                <Label htmlFor="num_travelers">Número de viajeros *</Label>
                <Input 
                  id="num_travelers"
                  type="number"
                  min="1"
                  value={bookingData.num_travelers}
                  onChange={(e) => setBookingData({ ...bookingData, num_travelers: parseInt(e.target.value) })}
                  required
                />
              </div>

              <div>
                <Label htmlFor="price">Precio (€)</Label>
                <Input 
                  id="price"
                  type="number"
                  min="0"
                  step="0.01"
                  value={bookingData.price}
                  onChange={(e) => setBookingData({ ...bookingData, price: e.target.value })}
                  placeholder="Ej: 2500.00"
                />
              </div>

              <div className="col-span-2">
                <Label htmlFor="budget">Presupuesto del cliente</Label>
                <Input 
                  id="budget"
                  value={bookingData.budget}
                  onChange={(e) => setBookingData({ ...bookingData, budget: e.target.value })}
                  placeholder="Ej: 3000-3500 EUR"
                />
              </div>

              <div className="col-span-2">
                <Label htmlFor="special_requests">Notas / Solicitudes especiales</Label>
                <Textarea 
                  id="special_requests"
                  value={bookingData.special_requests}
                  onChange={(e) => setBookingData({ ...bookingData, special_requests: e.target.value })}
                  rows={4}
                  placeholder="Detalles del mensaje y solicitudes especiales..."
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenDialog(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleCreateBooking}
              disabled={!bookingData.trip_title || !bookingData.travel_date}
              className="bg-green-600 hover:bg-green-700"
            >
              Crear Reserva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
